import type { SerializedState } from "@2048/game-core";

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** In-memory fallback when localStorage is unavailable (private mode, etc.). */
function createFakeStorage(): StorageLike {
  const data = new Map<string, string>();
  return {
    getItem: (key) => data.get(key) ?? null,
    setItem: (key, value) => {
      data.set(key, value);
    },
    removeItem: (key) => {
      data.delete(key);
    },
  };
}

function localStorageSupported(): boolean {
  try {
    const testKey = "__2048_test__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Persistence for classic play. Storage keys are kept identical to the original
 * site (`bestScore`, `gameState`, `noticeClosed`, `browserLanguage`) so existing
 * players keep their progress; `bestTile` is the one new key.
 */
export class LocalStorageManager {
  private readonly storage: StorageLike;

  private readonly bestScoreKey = "bestScore";
  private readonly bestTileKey = "bestTile";
  private readonly gameStateKey = "gameState";
  private readonly noticeClosedKey = "noticeClosed";
  private readonly browserLanguageKey = "browserLanguage";
  private readonly themeKey = "theme";

  constructor() {
    this.storage = localStorageSupported()
      ? window.localStorage
      : createFakeStorage();
  }

  getBestScore(): number {
    return Number(this.storage.getItem(this.bestScoreKey)) || 0;
  }

  setBestScore(score: number): void {
    this.storage.setItem(this.bestScoreKey, String(score));
  }

  getBestTile(): number {
    return Number(this.storage.getItem(this.bestTileKey)) || 0;
  }

  setBestTile(tile: number): void {
    this.storage.setItem(this.bestTileKey, String(tile));
  }

  getGameState(): SerializedState | null {
    const json = this.storage.getItem(this.gameStateKey);
    return json ? (JSON.parse(json) as SerializedState) : null;
  }

  setGameState(state: SerializedState): void {
    this.storage.setItem(this.gameStateKey, JSON.stringify(state));
  }

  clearGameState(): void {
    this.storage.removeItem(this.gameStateKey);
  }

  getNoticeClosed(): boolean {
    return this.storage.getItem(this.noticeClosedKey) === "true";
  }

  setNoticeClosed(closed: boolean): void {
    this.storage.setItem(this.noticeClosedKey, String(closed));
  }

  getBrowserLanguage(): string | null {
    return this.storage.getItem(this.browserLanguageKey);
  }

  setBrowserLanguage(language: string): void {
    this.storage.setItem(this.browserLanguageKey, language);
  }

  /** Theme preference: "light" | "dark" | "system" (null when never set). */
  getTheme(): string | null {
    return this.storage.getItem(this.themeKey);
  }

  setTheme(theme: string): void {
    this.storage.setItem(this.themeKey, theme);
  }
}
